import { Component } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, Event as RouterEvent, NavigationEnd, NavigationError } from '@angular/router';
import { OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { initFlowbite } from 'flowbite';
import { LoadingService } from './@services/loading.service';
import { AuthService } from './@services/auth.service';
import { ClientNotificationsService } from './@services/client-notifications.service';
import { UserPrefsService, LastSeenKind } from './@services/user-prefs.service';
import { UserPreferencesService, PinnedScanner } from './@services/user-preferences.service';
import { CustomerColorsService } from './@services/customer-colors.service';
import { SyncOverlayComponent } from './admin/shared/sync-overlay.component';
import { AccountMenuComponent, AccountMenuItem } from 'f2tech-shared/account-menu.component';
import { AppTitleStrategy } from './title-strategy';
import { FaviconBadgeService } from './@services/favicon-badge.service';
import { AgreementReviewBadgeService } from './@services/agreement-review-badge.service';

//AG Grid
import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from "ag-grid-enterprise";
// Register all Community features
ModuleRegistry.registerModules([AllEnterpriseModule]);
LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-078800}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{F2_Tech_LLC}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{F2}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{F2}_need_to_be_licensed___{F2}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{7_March_2026}____[v3]_[0102]_MTc3Mjg0MTYwMDAwMA==be208ca7a079cc86b5f418aba132fd62");

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, RouterLinkActive, CommonModule, FormsModule, SyncOverlayComponent, AccountMenuComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  
  private subs: any = [];
  title = 'F2 Tech';
  is_loading = false;

  notifications: any[] = [];

  // Bound once so the template's async pipe doesn't create a new
  // Observable per change-detection tick. Assigned in the constructor
  // body since field initializers run before parameter properties.
  userPrefsState$!: ReturnType<UserPrefsService['state$']>;

  constructor(
    private router: Router,
    public loadingService: LoadingService,
    public authService: AuthService,
    private notifyService: ClientNotificationsService,
    public userPrefs: UserPrefsService,
    public userPreferences: UserPreferencesService,
    private customerColors: CustomerColorsService,
    private titleStrategy: AppTitleStrategy,
    private faviconBadge: FaviconBadgeService,
    public reviewBadge: AgreementReviewBadgeService,
  ) {
    this.userPrefsState$ = this.userPrefs.state$();
    // IT-F2-426 c/2795820a (Mike 2026-10-02): kick off the "Agreement
    // Review" sidenav badge poll. Service self-silences on failure so
    // a 60s background refresh is cheap. Review page bump()s this
    // whenever a decision write changes the queue, so the badge
    // reflects fresh state well under the 60s poll floor.
    this.reviewBadge.start();
  }

  /**
   * Sidenav badge text — '' below 1, '1'..'9' literal, '9+' otherwise.
   * Caps mirror the server-side count cap (10) so the rendering is
   * consistent across the round-trip.
   */
  badgeText(n: number | null | undefined): string {
    const v = Math.floor(n || 0);
    if (v <= 0) return '';
    if (v >= 10) return '9+';
    return String(v);
  }

  /**
   * Called by the nav-link click handler on the Error Reports and
   * Commerce Events tiles. Snapshots the current last_seen for the
   * destination panel's NEW-row highlight, then bumps server-side
   * + cross-tab so all open admin tabs clear the badge in unison.
   */
  async onNavToTrackedPanel(kind: LastSeenKind): Promise<void> {
    this.userPrefs.beforeMarkSeen(kind);
    await this.userPrefs.markSeen(kind);
  }

  /**
   * Sidebar Pinned-section open-in-new-tab handler. Uses the same-origin
   * sso-redirect endpoint so the browser lands on the target scanner
   * WITH a fresh sid — server-side mint means we don't have to await
   * anything from the SPA. (IT-F2-52 polish)
   */
  openPinnedInNewTab(pin: PinnedScanner, evt: MouseEvent): void {
    evt?.preventDefault?.();
    if (!pin?.url) return;
    window.open(this.userPreferences.ssoRedirectUrl(pin.url), '_blank', 'noopener');
  }

  /**
   * `<f2-account-menu>` accepts optional cross-app links via customItems.
   * f2-admin only surfaces sign-out + change-password (no gallery hop —
   * admins don't need a per-customer /scans launcher from here). Return
   * an empty array so the shared component knows to render just the two
   * built-in actions.
   */
  crossAppMenuItems(): AccountMenuItem[] { return []; }

  /**
   * Change password affordance from the account menu. Hard-navs to the
   * shared f2-members forgot-password form (which is proxied at
   * admin.f2-tech.ai/forgot-password via vercel.json). Prefills the
   * email so the operator doesn't have to retype it.
   */
  onChangePassword(): void {
    const email = encodeURIComponent(this.authService.get_email() || '');
    const qs = email ? `?email=${email}` : '';
    window.location.assign(`/forgot-password${qs}`);
  }

  ngOnInit(): void {
    initFlowbite();
    this.router.events.subscribe((event: RouterEvent) => {
      if (event instanceof NavigationEnd) {
        setTimeout(() => {
          initFlowbite();
        }, 100);
        // IT-F2-426 c/<chunk-reload> (Mike 2026-10-05): clear the
        // reload guard once a navigation finishes cleanly, so a FUTURE
        // chunk-load error after another redeploy still auto-recovers.
        try { sessionStorage.removeItem('__chunk_reload_once__'); } catch { /* ignore */ }
      }
      // IT-F2-426 c/<chunk-reload> (Mike 2026-10-05): when Vercel redeploys
      // f2-admin, the hashed lazy-chunk filenames change. Any tab left open
      // on the previous deploy will try to lazy-load a chunk that no longer
      // exists → Vercel's SPA fallback returns index.html (737B HTML with
      // content-type:text/html) instead of a 404, which the browser's module
      // loader can't parse → silent navigation failure. Caught Mike mid-
      // session with chunk-M2KL6EXT.js missing on 2026-10-05 HAR. Force a
      // reload to pick up the new index.html + its fresh chunk refs.
      if (event instanceof NavigationError) {
        const msg = String(event.error?.message || event.error || '');
        if (/ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module/i.test(msg)
            && typeof window !== 'undefined' && !sessionStorage.getItem('__chunk_reload_once__')) {
          sessionStorage.setItem('__chunk_reload_once__', String(Date.now()));
          window.location.reload();
        }
      }
    });

    this.subs = [
      this.loadingService.visible$.subscribe((res: any) => {
        // Defer to next tick: a synchronous loadingService.add() during route
        // guard / app init fires this subscriber mid-change-detection cycle,
        // tripping Angular's NG0100 ExpressionChangedAfterItHasBeenChecked
        // check on the *ngIf="is_loading" at app.component.html:2.
        setTimeout(() => this.is_loading = res, 0);
      }),
      this.notifyService.notifications.subscribe((res: any) => {
        if (res?.err) {
          this.notifications = [];
          return;
        }
        this.notifications = res;
      }),
      // Push the current max sidenav-unread count into the title
      // strategy AND the favicon overlay. AppComponent (outside the
      // router-pipeline injection chain) is the safe place to
      // subscribe to UserPrefsService without inducing the
      // Router → TitleStrategy → UserPrefsService → Router cycle.
      // Title carries the ♪N prefix (visible in tab titles + task
      // switchers); favicon carries the red circle badge (the visual
      // cue operators recognize from gmail/slack/linear). Mike
      // 2026-07-03.
      this.userPrefsState$.subscribe((state: any) => {
        const er = Number(state?.unread?.error_reports || 0);
        const ce = Number(state?.unread?.commerce_events || 0);
        const max = Math.max(er, ce);
        this.titleStrategy.setBadge(max);
        void this.faviconBadge.setBadge(max);
      }),
    ];

    // Kick the per-operator unread badges + customer-color hydrate as
    // soon as auth is settled.
    //
    // AuthService.bootstrap() is async (fires from the route guard's
    // canActivate), so `is_logged_in()` is almost always false at
    // ngOnInit — the synchronous check here misses on every fresh
    // page load, meaning the initial GET /user-prefs/last-seen never
    // fires and the sidenav badge stays at 0 (badge is gated by
    // `unread.error_reports > 0`). Only the mark-seen POSTs kept
    // hitting because those are triggered by later user clicks after
    // auth has settled. Mike 2026-07-02: "we still aren't seeing
    // count badges on the errors report the admin/client apps or on
    // the commerce events admin app".
    //
    // Fix: retry on `user_changed` too. First non-null user event
    // triggers start() — subsequent events (logout / re-login) are
    // idempotent because UserPrefsService guards its own re-start.
    const kickOnce = () => {
      if (!this.authService.is_logged_in()) return;
      this.userPrefs.start();
      this.customerColors.hydrate();
      // Load pinned-scanner preferences so the sidebar renders as soon
      // as the auth handshake completes. Idempotent — loadOnce() is a
      // no-op after the first success. (IT-F2-52)
      void this.userPreferences.loadOnce();
    };
    kickOnce();
    this.subs.push(
      this.authService.user_changed.subscribe(() => kickOnce()),
    );
  }
  ngOnDestroy(): void {
    if (this.subs?.length) {
      this.subs.forEach((sub: any) => {
        sub.unsubscribe();
      });
    }
  }
}
